/**
 * PaymentProcess.jsx — Payment via Converge Lightbox + CSDK Terminal
 *
 * KEY_IN: Lightbox opens directly. Our Modal hides while Lightbox is visible,
 *         reappears only to show the result (success/declined).
 * SWIPE:  CSDK local terminal polling.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { getAxiosInstance } from '../../../util/service';
import { moduleTypes } from '../../../util/constants';

var LOADING    = 'LOADING';
var READY      = 'READY';
var PROCESSING = 'PROCESSING';
var COMPLETING = 'COMPLETING';
var SUCCESS    = 'SUCCESS';
var FAILED     = 'FAILED';
var ERROR      = 'ERROR';

var PaymentProcess = function (props) {

    var invoiceNumber   = props.invoiceNumber;
    var paymentAmount   = props.paymentAmount;
    var paymentLocation = props.paymentLocation;
    var customerInfo    = props.customerInfo;
    var onComplete      = props.onComplete;
    var onCancel        = props.onCancel;

    var stateArr        = useState(LOADING);
    var state           = stateArr[0];
    var setState        = stateArr[1];

    var msgArr          = useState('Initializing payment...');
    var statusMessage   = msgArr[0];
    var setMessage      = msgArr[1];

    var errorArr        = useState(null);
    var error           = errorArr[0];
    var setError        = errorArr[1];

    var resultArr       = useState(null);
    var result          = resultArr[0];
    var setResult       = resultArr[1];

    var intentArr       = useState(null);
    var intentUuid      = intentArr[0];
    var setIntent       = intentArr[1];

    var tokenArr        = useState(null);
    var sessionToken    = tokenArr[0];
    var setSessionToken = tokenArr[1];

    var csdkArr         = useState({});
    var csdkConfig      = csdkArr[0];
    var setCsdkConfig   = csdkArr[1];

    var swipeArr        = useState(true);
    var swipeEnabled    = swipeArr[0];
    var setSwipeEnabled = swipeArr[1];

    var keyInArr        = useState(true);
    var keyInEnabled    = keyInArr[0];
    var setKeyInEnabled = keyInArr[1];

    var pollCountArr    = useState(0);
    var pollCount       = pollCountArr[0];
    var setPollCount    = pollCountArr[1];

    // Controls visibility of our Modal.
    // Hidden while Lightbox is open so user sees ONE popup, not two.
    var modalArr        = useState(true);
    var showModal       = modalArr[0];
    var setShowModal    = modalArr[1];

    var pollingRef      = useRef(null);
    var gatewayIdRef    = useRef(null);
    var chanIdRef       = useRef(null);
    var scriptLoaded    = useRef(false);

    var formatAmount = function (amt) {
        return parseFloat(amt || paymentAmount || 0).toFixed(2);
    };

    var stopPolling = function () {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    // ─── Cancel active transaction on the terminal (frees the device) ────
    var cancelTerminalTransaction = function (localUrl) {
        var gwId = gatewayIdRef.current;
        var chId = chanIdRef.current;
        if (!gwId || !chId) return;

        fetch(localUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                method: 'cancelPaymentTransaction',
                requestId: 'REQ-' + Date.now(),
                targetType: 'paymentGatewayConverge',
                version: '1.0',
                parameters: {
                    paymentGatewayId: gwId,
                    chanId: chId
                }
            })
        }).catch(function (e) { /* best effort */ });
        chanIdRef.current = null;
    };

    // ─── Cancel active intent so it doesn't block retry ─────────────────

    var cancelActiveIntent = function () {
        if (!intentUuid) return Promise.resolve();
        return getAxiosInstance(moduleTypes.DISPATCH)
            .post('api/payment/cancel', { intentUuid: intentUuid })
            .catch(function (err) {
                console.warn('Cancel intent failed (non-blocking):', err);
            });
    };

    // ─── 1. Initiate Payment ───────────────────────────────────────────

    var initiate = useCallback(function () {
        setState(LOADING);
        setMessage('Preparing payment...');
        setError(null);
        setResult(null);
        setPollCount(0);
        setShowModal(true);

        var payload = {
            invoiceNumber: invoiceNumber,
            amount: paymentAmount,
            paymentMethod: 'KEY_IN',
            paymentLocation: paymentLocation,
            customerFirstName: customerInfo ? customerInfo.firstName : undefined,
            customerLastName:  customerInfo ? customerInfo.lastName : undefined,
            customerAddress:   customerInfo ? customerInfo.address : undefined,
            customerCity:      customerInfo ? customerInfo.city : undefined,
            customerState:     customerInfo ? customerInfo.state : undefined,
            customerZip:       customerInfo ? customerInfo.zip : undefined,
            customerEmail:     customerInfo ? customerInfo.email : undefined,
            customerPhone:     customerInfo ? customerInfo.phone : undefined,
            idempotencyKey:    invoiceNumber + '-' + Date.now()
        };

        getAxiosInstance(moduleTypes.DISPATCH)
            .post('api/payment/initiate', payload)
            .then(function (resp) {
                if (!resp.data || !resp.data.success) {
                    throw new Error((resp.data && resp.data.message) || 'Failed to initiate payment');
                }

                var data = resp.data.data;
                setIntent(data.intentUuid);
                setSessionToken(data.sessionToken || null);
                setCsdkConfig(data.csdkConfig || {});
                setSwipeEnabled(data.swipeEnabled !== false);
                setKeyInEnabled(data.keyInEnabled !== false);

                var lightboxUrl = data.lightboxJsUrl || data.checkoutJsUrl;
                if (lightboxUrl) {
                    return loadLightboxScript(lightboxUrl);
                }
            })
            .then(function () {
                setState(READY);
                setMessage('');
            })
            .catch(function (err) {
                console.error('Payment initiation failed:', err);
                var msg = 'Failed to start payment';
                if (err.response && err.response.data && err.response.data.message) {
                    msg = err.response.data.message;
                } else if (err.message) {
                    msg = err.message;
                }
                setError(msg);
                setState(ERROR);
                setMessage('Payment initialization failed');
            });
    }, [invoiceNumber, paymentAmount, paymentLocation, customerInfo]);

    useEffect(function () { initiate(); }, [initiate]);

    // Keep a ref so cleanup always has the latest intentUuid
    var intentRef = useRef(null);
    useEffect(function () {
        intentRef.current = intentUuid;
        // Store in sessionStorage so we can cancel on reload
        if (intentUuid) {
            sessionStorage.setItem('activePaymentIntent', intentUuid);
        }
    }, [intentUuid]);

    useEffect(function () {
        return function () {
            stopPolling();
            // Cancel any active terminal transaction on unmount so the device is freed
            var localUrl = (csdkConfig && csdkConfig.localUrl) || 'https://localhost:9790/rest/command';
            cancelTerminalTransaction(localUrl);
            closeGateway(localUrl);
        };
    }, []);

    // ─── On mount: cancel any leftover intent from previous load ───────
    useEffect(function () {
        var staleIntent = sessionStorage.getItem('activePaymentIntent');
        if (staleIntent) {
            console.log('Cancelling stale intent from previous session:', staleIntent);
            sessionStorage.removeItem('activePaymentIntent');
            getAxiosInstance(moduleTypes.DISPATCH)
                .post('api/payment/cancel', { intentUuid: staleIntent })
                .catch(function (err) {
                    console.warn('Stale intent cancel failed (non-blocking):', err);
                });
        }
    }, []);

    // ─── Cleanup on unmount (React navigation away) ────────────────────
    useEffect(function () {
        return function () {
            if (intentRef.current) {
                cancelActiveIntent();
                sessionStorage.removeItem('activePaymentIntent');
            }
        };
    }, []);

    // ─── 2. Load PayWithConverge.js ────────────────────────────────────

    var loadLightboxScript = function (url) {
        return new Promise(function (resolve, reject) {
            if (window.PayWithConverge) {
                scriptLoaded.current = true;
                resolve();
                return;
            }

            var existing = document.querySelector('script[src="' + url + '"]');
            if (existing) {
                var check = setInterval(function () {
                    if (window.PayWithConverge) {
                        clearInterval(check);
                        scriptLoaded.current = true;
                        resolve();
                    }
                }, 100);
                setTimeout(function () { clearInterval(check); reject(new Error('Lightbox script load timeout')); }, 15000);
                return;
            }

            var script = document.createElement('script');
            script.src = url;
            script.async = true;
            script.onload = function () {
                scriptLoaded.current = true;
                console.log('PayWithConverge.js loaded');
                resolve();
            };
            script.onerror = function () { reject(new Error('Failed to load PayWithConverge.js')); };
            document.head.appendChild(script);
        });
    };

    // ─── 3. KEY_IN: Lightbox ───────────────────────────────────────────
    //
    // Hide our Modal → open Lightbox → callback → show Modal with result.
    // Lightbox iframe is injected into <body> by Elavon, outside React's
    // #root, so aria-hidden on #root does NOT block it.

    var handleKeyInClick = function () {
        if (!window.PayWithConverge || !sessionToken) {
            setError('Payment system not ready. Please try again.');
            setState(ERROR);
            setMessage('Payment Error');
            return;
        }

        setError(null);

        // Hide our Modal — Lightbox becomes the only visible popup
        setShowModal(false);

        // Small delay to let Modal animation finish before opening Lightbox
        setTimeout(function () {
            var paymentFields = {
                ssl_txn_auth_token: sessionToken
            };

            var callback = {
                onError: function (errResponse) {
                    console.error('Lightbox ERROR:', errResponse);
                    var msg = 'Payment processing error';
                    if (typeof errResponse === 'string') {
                        msg = errResponse;
                    } else if (errResponse && errResponse.errorMessage) {
                        msg = errResponse.errorMessage;
                    }
                    setShowModal(true);
                    setError(msg);
                    setState(ERROR);
                    setMessage('Payment Error');
                },
                onDeclined: function (response) {
                    console.log('Lightbox DECLINED:', response);
                    console.log('=== ELAVON RESPONSE ===', JSON.stringify(response, null, 2));
                    setShowModal(true);
                    completePayment(response, 'onDeclined');
                },
                onApproval: function (response) {
                    console.log('Lightbox APPROVED:', response);
                    console.log('=== ELAVON RESPONSE ===', JSON.stringify(response, null, 2));
                    setShowModal(true);
                    completePayment(response, 'onApproval');
                },
                onCancelled: function () {
                    console.log('Lightbox CANCELLED');
                    setShowModal(true);
                    // Cancel old intent, then get fresh token
                    cancelActiveIntent().then(function () {
                        initiate();
                    });
                }
            };

            try {
                console.log('Opening PayWithConverge lightbox...');
                window.PayWithConverge.open(paymentFields, callback);
            } catch (err) {
                console.error('PayWithConverge.open() failed:', err);
                setShowModal(true);
                setError('Failed to open payment form: ' + err.message);
                setState(ERROR);
                setMessage('Payment Error');
            }
        }, 300);
    };

    // ─── 4. SWIPE: CSDK Terminal ───────────────────────────────────────

    /**
     * Parse CSDK response, checking HTTP status first.
     * Throws on non-OK HTTP status or non-JSON body.
     */
    var parseCsdkResponse = function (response) {
        if (!response.ok) {
            return response.text().then(function (body) {
                throw new Error('CSDK returned HTTP ' + response.status + ': ' + body.substring(0, 200));
            });
        }
        return response.json().catch(function () {
            throw new Error('CSDK returned non-JSON response (HTTP ' + response.status + ')');
        });
    };

    var handleSwipeClick = function () {
        // Guard against double-click
        if (state === PROCESSING) return;

        setState(PROCESSING);
        setMessage('Connecting to terminal...');
        setError(null);
        setPollCount(0);
        stopPolling(); // clear any leftover interval from a previous attempt

        var localUrl = csdkConfig.localUrl || 'https://localhost:9790/rest/command';

        var openReq = {
            method: 'openPaymentGateway',
            requestId: 'REQ-' + Date.now(),
            targetType: 'paymentGatewayConverge',
            version: '1.0',
            parameters: {
                merchantId: csdkConfig.merchantId,
                userId: csdkConfig.userId,
                pin: csdkConfig.pin,
                vendorId: csdkConfig.vendorId || '',
                vendorAppName: csdkConfig.vendorAppName || 'ACT',
                vendorAppVersion: csdkConfig.vendorAppVersion || '1.0',
                paymentGatewayEnvironment: csdkConfig.environment || 'DEMO',
                handleDigitalSignature: true
            }
        };

        fetch(localUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(openReq)
        })
        .then(parseCsdkResponse)
        .then(function (data) {
            var gatewayResult = data.data &&
                data.data.paymentGatewayCommand &&
                data.data.paymentGatewayCommand.openPaymentGatewayData;

            if (!gatewayResult || gatewayResult.result !== 'SUCCESS') {
                var detail = (gatewayResult && gatewayResult.errorMessage)
                    || (data.data && data.data.error)
                    || data.statusDetails
                    || 'Unknown error';
                throw new Error('Failed to connect to terminal: ' + detail);
            }

            var gwId = gatewayResult.paymentGatewayId;
            gatewayIdRef.current = gwId;
            setMessage('Present card on terminal...');

            var amountInCents = Math.round(parseFloat(paymentAmount) * 100);
            var txnReq = {
                method: 'startPaymentTransaction',
                requestId: 'REQ-' + Date.now(),
                targetType: 'paymentGatewayConverge',
                version: '1.0',
                parameters: {
                    paymentGatewayId: gwId,
                    transactionType: 'SALE',
                    tokenizedCardNumber: null,
                    tenderedAmount: { value: 10, currencyCode: 'USD' },
                    originalTransId: null,
                    generateToken: false,
                    gratuityFlowEnabled: false,
                    cardType: 'credit',
                    discount: null,
                    partialApprovalAllowed: csdkConfig.allowPartialAuth === 'true',
                    baseTransactionAmount: { value: amountInCents, currencyCode: 'USD' },
                    addToken: false,
                    manual: false,
                    gratuityQuickSelections: null,
                    invoiceNumber: invoiceNumber,
                    isTaxInclusive: false,
                    taxAmounts: { value: 0, currencyCode: 'USD' },
                    gratuityAmount: { value: 0, currencyCode: 'USD' },
                    avsFields: null,
                    gratuityCustomAmountEntryAllowed: false,
                    tenderType: 'CARD',
                    note: null,
                    forced: false
                }
            };

            return fetch(localUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(txnReq)
            });
        })
        .then(parseCsdkResponse)
        .then(function (txnData) {
            // ACT checks: rsp.statusDetails === "REQUEST_ACCEPTED"
            if (txnData.statusDetails && txnData.statusDetails !== 'REQUEST_ACCEPTED') {
                var errDetail = (txnData.data && txnData.data.error)
                    || txnData.statusDetails
                    || 'Unknown error';
                throw new Error('Terminal rejected transaction: ' + errDetail);
            }

            var txnResult = txnData.data &&
                txnData.data.paymentGatewayCommand;
            var chanId = txnResult && txnResult.chanId;
            chanIdRef.current = chanId;

            if (!chanId) {
                var errDetail2 = (txnResult && txnResult.errorMessage)
                    || (txnData.data && txnData.data.error)
                    || txnData.statusDetails
                    || 'Unknown error';
                throw new Error('Failed to start transaction on terminal: ' + errDetail2);
            }

            // Capture gwId locally so polls are safe even if ref is cleared
            var pollGwId = gatewayIdRef.current;
            var count = 0;
            var consecutiveErrors = 0;
            var MAX_CONSECUTIVE_ERRORS = 5;
            pollingRef.current = setInterval(function () {
                // If gateway was closed (e.g. by error handler), stop polling
                if (!pollingRef.current) return;

                count++;
                setPollCount(count);

                if (count >= 120) {
                    stopPolling();
                    handleCsdkError('Transaction timed out after 2 minutes. The terminal may not be responding.');
                    return;
                }

                var statusReq = {
                    method: 'getPaymentTransactionStatus',
                    requestId: 'REQ-' + Date.now(),
                    targetType: 'paymentGatewayConverge',
                    version: '1.0',
                    parameters: { paymentGatewayId: pollGwId, chanId: chanId }
                };

                fetch(localUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(statusReq)
                })
                .then(function (r) {
                    if (!r.ok) {
                        return r.text().then(function (body) {
                            console.error('Status poll HTTP ' + r.status + ':', body.substring(0, 200));
                            // Try to parse as JSON even from non-OK responses
                            // (CSDK often returns 404 with valid JSON body)
                            try { return JSON.parse(body); } catch (e) { return null; }
                        });
                    }
                    return r.json().catch(function () { return null; });
                })
                .then(function (statusData) {
                    if (!statusData) {
                        consecutiveErrors++;
                        setMessage('Waiting for terminal... (no response)');
                        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                            stopPolling();
                            handleCsdkError('Terminal stopped responding after ' + consecutiveErrors + ' attempts. Please check the terminal and try again.');
                        }
                        return;
                    }

                    // Reset consecutive error counter on any parseable response
                    consecutiveErrors = 0;

                    // ── Case 1: Top-level completed flag (seen when CSDK returns
                    //    flat response like {data: {completed: true, error: "..."}} )
                    if (statusData.data && statusData.data.completed === true) {
                        // Check if it's an error completion (no transaction was active)
                        if (statusData.data.error) {
                            stopPolling();
                            var topError = statusData.data.error;
                            var detail = statusData.statusDetails ? ' (' + statusData.statusDetails + ')' : '';
                            handleCsdkError('Terminal error: ' + topError + detail);
                            return;
                        }
                    }

                    // ── Case 2: INVALID_METHOD_TYPE at top level
                    if (statusData.statusDetails === 'INVALID_METHOD_TYPE') {
                        stopPolling();
                        handleCsdkError('Terminal rejected the status request. The transaction may not have started. Please try again.');
                        return;
                    }

                    // ── Case 3: Normal CSDK response — check paymentGatewayCommand.completed
                    //    (matches ACT reference: result.data.paymentGatewayCommand.completed)
                    var gwCmd = statusData.data && statusData.data.paymentGatewayCommand;

                    if (gwCmd && gwCmd.completed === true) {
                        stopPolling();
                        // Transaction data is at paymentTransactionData (ACT path)
                        // or getPaymentTransactionStatusData (alternate path)
                        var txnResult = gwCmd.paymentTransactionData
                            || gwCmd.getPaymentTransactionStatusData;
                        handleCsdkComplete(txnResult || gwCmd, localUrl);
                        return;
                    }

                    // ── Case 4: Transaction in progress — update UI with terminal status
                    var eventQueue = gwCmd && gwCmd.eventQueue;
                    var txnStatusData = gwCmd && (gwCmd.paymentTransactionData || gwCmd.getPaymentTransactionStatusData);
                    var statusMsg = (txnStatusData && txnStatusData.status)
                        || (eventQueue && eventQueue.length > 0 && eventQueue[eventQueue.length - 1].type);
                    if (statusMsg) {
                        if (statusMsg === 'WAITING_FOR_CARD' || statusMsg === 'WAITING_FOR_CARD_SWIPE') {
                            setMessage('Present card on terminal...');
                        } else if (statusMsg === 'WAITING_FOR_PIN') {
                            setMessage('Enter PIN on terminal...');
                        } else if (statusMsg === 'PROCESSING' || statusMsg === 'AUTHORIZING') {
                            setMessage('Processing payment...');
                        } else if (statusMsg === 'WAITING_FOR_SIGNATURE') {
                            setMessage('Signature required on terminal...');
                        } else if (statusMsg === 'CARD_REMOVED_TOO_EARLY') {
                            setMessage('Card removed too early. Please try again.');
                        } else if (statusMsg === 'CARD_ERROR' || statusMsg === 'CARD_BLOCKED') {
                            stopPolling();
                            handleCsdkError('Card error: ' + statusMsg + '. Please try a different card.');
                            return;
                        } else {
                            setMessage('Terminal: ' + statusMsg);
                        }
                    }
                })
                .catch(function (err) {
                    console.error('Polling error:', err);
                    consecutiveErrors++;
                    if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                        stopPolling();
                        handleCsdkError('Lost connection to terminal. Please check that Converge Connect is running and try again.');
                    }
                });
            }, 1000);
        })
        .catch(function (err) {
            console.error('CSDK error:', err);
            var msg = err.message || 'Terminal connection failed';
            // "Failed to fetch" means CSDK/Converge Connect is not running locally
            if (msg.toLowerCase().indexOf('failed to fetch') !== -1
                || msg.toLowerCase().indexOf('networkerror') !== -1
                || msg.toLowerCase().indexOf('network error') !== -1) {
                msg = 'Cannot connect to card reader. Please ensure Converge Connect is installed and running on this computer, and the card reader is plugged in.';
            }
            handleCsdkError(msg);
        });
    };

    var handleCsdkComplete = function (txnStatus, localUrl) {
        var result = txnStatus.result || '';
        var isApproved = result === 'APPROVED' || result === 'PARTIALLY_APPROVED';
        var isFailed = result === 'FAILED';

        // Extract error details for FAILED transactions
        var errorMessages = txnStatus.errors || [];
        var userFriendlyError = '';
        if (errorMessages.length > 0) {
            // Use the most readable error (usually the last one)
            userFriendlyError = errorMessages[errorMessages.length - 1] || errorMessages[0] || '';
        }

        closeGateway(localUrl);

        if (isFailed) {
            // Don't send FAILED (permission/config errors) to backend as a payment result
            console.error('CSDK transaction FAILED:', errorMessages);
            setError(userFriendlyError || 'Transaction failed on terminal.');
            setResult({
                status: 'FAILED',
                resultMessage: result,
                errorDetails: errorMessages.join('; ')
            });
            setState(ERROR);
            setMessage('Transaction Failed');
            return;
        }

        var payload = {
            intentUuid: intentUuid,
            sslResult: isApproved ? '0' : '1',
            sslResultMessage: result,
            sslTxnId: txnStatus.id || txnStatus.transactionId || '',
            sslApprovalCode: txnStatus.authCode || txnStatus.hostReferenceNumber || '',
            sslCardNumber: txnStatus.maskedPan || '',
            sslCardType: txnStatus.cardScheme || '',
            callbackType: 'csdk',
            rawResponse: JSON.stringify(txnStatus)
        };

        getAxiosInstance(moduleTypes.DISPATCH)
            .post('api/payment/complete', payload)
            .catch(function (err) {
                console.error('Failed to post CSDK result:', err);
            });

        setResult({
            status: isApproved ? 'COMPLETED' : 'FAILED',
            txnId: txnStatus.id || txnStatus.transactionId,
            approvalCode: txnStatus.authCode || txnStatus.hostReferenceNumber,
            cardType: txnStatus.cardScheme,
            cardNumber: txnStatus.maskedPan,
            resultMessage: result,
            amount: txnStatus.approvedAmount ? (txnStatus.approvedAmount.value / 100) : paymentAmount
        });
        setState(isApproved ? SUCCESS : FAILED);
        setMessage(isApproved ? 'Payment Approved' : 'Payment Declined');
    };

    var closeGateway = function (localUrl) {
        if (!gatewayIdRef.current) return;
        var gwId = gatewayIdRef.current;
        gatewayIdRef.current = null; // clear ref immediately to prevent duplicate close
        fetch(localUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                method: 'closePaymentGateway',
                requestId: 'REQ-' + Date.now(),
                targetType: 'paymentGatewayConverge',
                version: '1.0',
                parameters: { paymentGatewayId: gwId }
            })
        }).catch(function (e) { /* best effort */ });
    };

    var handleCsdkError = function (msg) {
        stopPolling();
        var localUrl = csdkConfig.localUrl || 'https://localhost:9790/rest/command';
        cancelTerminalTransaction(localUrl);
        closeGateway(localUrl);
        setError(msg);
        setState(ERROR);
        setMessage('Terminal Error');
    };

    // ─── 5. Complete Payment (Lightbox callback) ───────────────────────

    var completePayment = function (elavonResponse, callbackType) {
        setState(COMPLETING);
        setMessage('Saving payment...');

        var r = elavonResponse || {};
        var payload = {
            intentUuid: intentUuid,
            sslResult:        r.ssl_result        || r.result        || '',
            sslResultMessage: r.ssl_result_message || r.result_message || r.errorMessage || r.errorName || '',
            sslTxnId:         r.ssl_txn_id         || r.txn_id         || '',
            sslApprovalCode:  r.ssl_approval_code  || r.approval_code  || '',
            sslCardNumber:    r.ssl_card_number    || r.card_number    || '',
            sslCardType:      r.ssl_card_type      || r.card_type      || '',
            sslExpDate:       r.ssl_exp_date       || r.exp_date       || '',
            sslAvsResponse:   r.ssl_avs_response   || r.avs_response   || '',
            sslCvv2Response:  r.ssl_cvv2_response  || r.cvv2_response  || '',
            callbackType:     callbackType,
            rawResponse:      JSON.stringify(r)
        };

        getAxiosInstance(moduleTypes.DISPATCH)
            .post('api/payment/complete', payload)
            .then(function (resp) {
                if (resp.data && resp.data.success) {
                    var data = resp.data.data;
                    setResult(data);
                    setState(data.status === 'COMPLETED' ? SUCCESS : FAILED);
                    setMessage(data.status === 'COMPLETED' ? 'Payment Approved' : 'Payment Declined');
                } else {
                    throw new Error((resp.data && resp.data.message) || 'Failed to save payment');
                }
            })
            .catch(function (err) {
                console.error('Payment completion failed:', err);
                var isApproved = (r.ssl_result || r.result || '') === '0';
                setResult({
                    status: isApproved ? 'COMPLETED' : 'FAILED',
                    txnId: r.ssl_txn_id || r.txn_id || '',
                    approvalCode: r.ssl_approval_code || r.approval_code || '',
                    resultMessage: r.ssl_result_message || r.result_message || r.errorMessage || r.errorName || '',
                    cardType: r.ssl_card_type || r.card_type || '',
                    cardNumber: r.ssl_card_number || r.card_number || ''
                });
                setState(isApproved ? SUCCESS : FAILED);
                if (isApproved) {
                    setError('Payment approved but failed to save. Contact support.');
                }
            });
    };

    // ─── 6. User actions ───────────────────────────────────────────────

    var handleDone = function () {
        sessionStorage.removeItem('activePaymentIntent');
        if (onComplete) onComplete(result);
    };

    var handleRetry = function () {
        stopPolling();
        var localUrl = csdkConfig.localUrl || 'https://localhost:9790/rest/command';
        cancelTerminalTransaction(localUrl);
        closeGateway(localUrl);
        setSessionToken(null);
        scriptLoaded.current = false;
        chanIdRef.current = null;
        cancelActiveIntent().then(function () {
            initiate();
        });
    };

    var handleCancel = function () {
        stopPolling();
        var localUrl = csdkConfig.localUrl || 'https://localhost:9790/rest/command';
        cancelTerminalTransaction(localUrl);
        closeGateway(localUrl);
        cancelActiveIntent().then(function () {
            sessionStorage.removeItem('activePaymentIntent');
            if (onCancel) onCancel();
        });
    };

    // ─── Render ────────────────────────────────────────────────────────
    // Modal hides when Lightbox is open, reappears for result.

    return (
        <Modal
            show={showModal}
            onHide={handleCancel}
            centered
            backdrop="static"
            size="lg"
        >
            <Modal.Header closeButton className="bg-light">
                <Modal.Title>Process Payment</Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-4">

                {/* Invoice & Amount */}
                <div className="text-center mb-4 p-3 bg-light rounded">
                    {invoiceNumber && (
                        <p className="text-muted mb-2">
                            Invoice: <strong>{invoiceNumber}</strong>
                        </p>
                    )}
                    <h2 className="text-primary mb-0">
                        ${formatAmount()}
                    </h2>
                </div>

                {/* ── LOADING / COMPLETING ────────────────────────────── */}
                {(state === LOADING || state === COMPLETING) && (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">{statusMessage}</p>
                    </div>
                )}

                {/* ── READY — Select Payment Method ──────────────────── */}
                {state === READY && (
                    <div className="py-3">
                        <p className="text-center text-muted mb-4">Select payment method</p>
                        <div className="d-flex flex-column" style={{ gap: '12px' }}>
                            {swipeEnabled && (
                                <Button
                                    variant="primary"
                                    size="lg"
                                    block
                                    className="d-flex align-items-center justify-content-center py-3"
                                    onClick={handleSwipeClick}
                                >
                                    <i className="fa fa-credit-card mr-3" style={{ fontSize: '20px' }}></i>
                                    Swipe / Tap / Insert Card
                                </Button>
                            )}
                            {keyInEnabled && (
                                <Button
                                    variant="outline-secondary"
                                    size="lg"
                                    block
                                    className="d-flex align-items-center justify-content-center py-3"
                                    onClick={handleKeyInClick}
                                >
                                    <i className="fa fa-keyboard-o mr-3" style={{ fontSize: '20px' }}></i>
                                    Enter Card Manually
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                {/* ── PROCESSING (swipe terminal wait) ───────────────── */}
                {state === PROCESSING && (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 mb-1">{statusMessage}</p>
                        {pollCount > 0 && pollCount < 60 && (
                            <small className="text-muted">Waiting for terminal... {pollCount}s</small>
                        )}
                        {pollCount >= 60 && (
                            <small className="text-warning">
                                Still waiting ({pollCount}s). If the terminal is unresponsive, click Cancel and try again.
                            </small>
                        )}
                    </div>
                )}

                {/* ── SUCCESS ────────────────────────────────────────── */}
                {state === SUCCESS && (
                    <div className="text-center py-4">
                        <div className="mb-3">
                            <i className="fa fa-check-circle text-success" style={{ fontSize: '72px' }}></i>
                        </div>
                        <h4 className="text-success mb-3">Payment Approved</h4>
                        {result && (
                            <div className="bg-light rounded p-3 text-left mx-auto" style={{ maxWidth: '320px' }}>
                                {result.cardType && (
                                    <p className="mb-1">
                                        <strong>Card:</strong> {result.cardType}
                                        {result.cardNumber ? ' ****' + result.cardNumber.slice(-4) : ''}
                                    </p>
                                )}
                                {result.txnId && (
                                    <p className="mb-1"><strong>Transaction ID:</strong> {result.txnId}</p>
                                )}
                                {result.approvalCode && (
                                    <p className="mb-0"><strong>Auth Code:</strong> {result.approvalCode}</p>
                                )}
                            </div>
                        )}
                        {error && (
                            <Alert variant="warning" className="mt-3 mx-auto" style={{ maxWidth: '320px' }}>
                                <small>{error}</small>
                            </Alert>
                        )}
                    </div>
                )}

                {/* ── FAILED ─────────────────────────────────────────── */}
                {state === FAILED && (
                    <div className="text-center py-4">
                        <div className="mb-3">
                            <i className="fa fa-times-circle text-danger" style={{ fontSize: '72px' }}></i>
                        </div>
                        <h4 className="text-danger mb-3">Payment Declined</h4>
                        <p className="text-muted">
                            {(result && result.resultMessage) || 'Transaction was not approved'}
                        </p>
                    </div>
                )}

                {/* ── ERROR ──────────────────────────────────────────── */}
                {state === ERROR && (
                    <div className="py-3">
                        <Alert variant="danger" className="text-center">
                            <i className="fa fa-exclamation-triangle mr-2" style={{ fontSize: '24px' }}></i>
                            <Alert.Heading className="h5 mt-2">{statusMessage}</Alert.Heading>
                            <p className="mb-0">{error}</p>
                        </Alert>
                    </div>
                )}

            </Modal.Body>

            <Modal.Footer className="bg-light">
                {(state === READY || state === LOADING || state === PROCESSING) && (
                    <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
                )}
                {state === COMPLETING && (
                    <Button variant="secondary" disabled>Please wait...</Button>
                )}
                {state === SUCCESS && (
                    <Button variant="success" onClick={handleDone}>Done</Button>
                )}
                {(state === FAILED || state === ERROR) && (
                    <>
                        <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
                        <Button variant="primary" onClick={handleRetry}>Try Again</Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default PaymentProcess;